import { useCallback, useEffect, useRef, useState } from 'react'
import { api, ApiError } from '../../lib/api'
import { sunSignOf } from '../../../../shared/astro'
import { SIGNS } from '../../data/signs'
import { loc } from '../../data/cards'
import { useSirr } from '../../store'
import { track } from '../../lib/analytics'
import { InterpretationBlock, type Text } from './Match'
import { BIRTH_RANGE, inputStyle, Row } from './Natal'

/**
 * Прогноз на день — астрологический сосед «карты дня».
 *
 * Считается по небу сегодняшнего дня и знаку Солнца человека, поэтому один и
 * тот же текст подходит всем с этим знаком: сервер держит его в кеше и не
 * тратит дневную квоту. Дату рождения берём из профиля — если человек уже
 * вводил её для натальной карты или совместимости, спрашивать снова незачем.
 */
export function Daily() {
  const { profile, lang, t, update } = useSirr()
  const [text, setText] = useState<Text>({ status: 'idle' })

  const birth = profile.birth
  const [draft, setDraft] = useState('')
  const sign = birth ? sunSignOf(birth.year, birth.month, birth.day) : null

  // Запрос идёт сам, как только известен знак: прогноз бесплатный, и лишнее
  // нажатие здесь ничего не защищает.
  const asked = useRef<string>('')

  const request = useCallback(async () => {
    if (!birth) return
    setText({ status: 'loading' })
    try {
      const res = await api.astro({
        kind: 'daily',
        lang,
        a: { year: birth.year, month: birth.month, day: birth.day },
        goals: profile.goals,
      })
      setText({ status: 'ok', text: res.text })
      track('astro_done', { kind: 'daily' })
    } catch (err) {
      if (err instanceof ApiError && err.code === 'limit_reached') setText({ status: 'limit' })
      else setText({ status: 'failed' })
    }
  }, [birth, lang, profile.goals])

  useEffect(() => {
    if (!birth) return
    const key = `${birth.year}-${birth.month}-${birth.day}-${lang}-${new Date().toDateString()}`
    if (asked.current === key) return
    asked.current = key
    void request()
  }, [birth, lang, request])

  /*
    Дату спрашиваем ПРЯМО ЗДЕСЬ, а не отсылаем за ней.

    Раньше на этом месте стояла одна подсказка «укажите дату рождения» — и всё:
    указать её на этой вкладке было нечем, а лежала она на соседней, в форме
    натальной карты, где спрашивают ещё время и место. То есть первый экран
    астрологии у человека без даты был тупиком с советом вместо действия.

    Кнопки «готово» тут нет намеренно: прогноз бесплатный и запрашивается сам,
    как только дата стала полной.
  */
  if (!birth) {
    return (
      <div>
        <p style={{ textAlign: 'center', color: 'var(--ink-faint)', fontSize: 13, lineHeight: 1.5 }}>
          {t('dailyNeedBirth')}
        </p>
        <div style={{ marginTop: 18 }}>
          <Row label={t('birthDate')}>
            <input
              type="date"
              value={draft}
              min={BIRTH_RANGE.min}
              max={BIRTH_RANGE.max}
              onChange={(e) => {
                setDraft(e.target.value)
                const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(e.target.value)
                if (!m) return
                // Время и место оставляем пустыми: прогнозу нужен только знак
                // Солнца, а натальная карта спросит недостающее сама.
                update({
                  birth: {
                    year: Number(m[1]),
                    month: Number(m[2]),
                    day: Number(m[3]),
                    hour: null,
                    minute: null,
                    place: null,
                    timeUnknown: false,
                  },
                })
              }}
              style={inputStyle}
            />
          </Row>
        </div>
      </div>
    )
  }

  return (
    <div>
      <div style={{ textAlign: 'center', marginBottom: 16 }}>
        <div className="eyebrow">{t('dailyTitle')}</div>
        <div style={{ fontFamily: 'var(--serif)', fontStyle: 'italic', fontSize: 22, marginTop: 6 }}>
          {sign !== null ? loc(SIGNS[sign], lang) : ''}
        </div>
        <div style={{ fontSize: 12.5, color: 'var(--ink-muted)', marginTop: 4 }}>{t('dailySub')}</div>
      </div>

      <InterpretationBlock text={text} onRetry={() => void request()} />
    </div>
  )
}
